// ═══════════════════════════════════════════════════════════════
// ArchivedCustomers — where archived customers wait, and the only
// place a customer can be destroyed for good.
//
// Permanent deletion used to sit on the customer page, one confirm
// away from an ordinary tap, and it took every vehicle and every job
// with it. Putting it here makes destroying someone a deliberate
// second act: archive first, then come looking.
//
// The export offered before deleting is the point of building the
// export feature first — the last chance to keep the record is
// attached to the moment it's about to be lost.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { getDocs } from 'firebase/firestore';
import { Archive, Undo2, Trash2, Download, Loader2 } from 'lucide-react';
import { customersCol, jobsCol, vehiclesCol } from './database.js';
import { restoreCustomer, purgeCustomerForever } from './lib/db-crud.js';
import { formatPhone, toDate } from './lib/job-helpers.js';
import ConfirmDialog from './ConfirmDialog.jsx';
import {
  customersCsv, vehiclesCsv, jobsCsv, downloadText, stamp,
} from './lib/export-data.js';

function when(ts) {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ArchivedCustomers({ customers }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [error, setError] = useState(null);
  // Jobs and vehicles are only needed for the counts and the export,
  // and only once the panel is open. Fetching them here instead of
  // adding two more live listeners to the Customers screen keeps the
  // common case — a mechanic who never archives anyone — free.
  const [loaded, setLoaded] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    if (!open || loaded) return;
    let alive = true;
    (async () => {
      try {
        const [j, v] = await Promise.all([getDocs(jobsCol()), getDocs(vehiclesCol())]);
        if (!alive) return;
        setJobs(j.docs.map((d) => ({ id: d.id, ...d.data() })));
        setVehicles(v.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoaded(true);
      } catch (e) {
        if (alive) setError(`Couldn't load their records: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, [open, loaded]);

  if (customers.length === 0) return null;

  function countsFor(c) {
    return {
      jobs: jobs.filter((j) => j.customerId === c.id).length,
      vehicles: vehicles.filter((v) => v.customerId === c.id).length,
    };
  }

  async function handleRestore(c) {
    setBusyId(c.id);
    setError(null);
    try {
      await restoreCustomer(customersCol(), c);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  // Everything about this one customer, in one file, right before
  // it stops existing.
  function exportOne(c) {
    const theirJobs = jobs.filter((j) => j.customerId === c.id);
    const theirVehicles = vehicles.filter((v) => v.customerId === c.id);
    const byId = { [c.id]: c };
    const vehicleById = Object.fromEntries(theirVehicles.map((v) => [v.id, v]));
    const safeName = (c.name || 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const text =
      `CUSTOMER\r\n${customersCsv([c])}\r\n` +
      `VEHICLES\r\n${vehiclesCsv(theirVehicles, byId)}\r\n` +
      `JOBS\r\n${jobsCsv(theirJobs, { customerById: byId, vehicleById })}`;
    downloadText(`bayless-${safeName}-${stamp()}.csv`, text);
  }

  async function handlePurge(c) {
    setBusyId(c.id);
    setError(null);
    try {
      await purgeCustomerForever(c.id);
      setJobs((prev) => prev.filter((j) => j.customerId !== c.id));
      setVehicles((prev) => prev.filter((v) => v.customerId !== c.id));
    } catch (e) {
      console.error('Purge failed:', e);
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="archived-panel">
      <button
        type="button"
        className="archived-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Archive size={15} />
        <span className="archived-head-text">
          <strong>{customers.length} archived</strong>
          <span className="muted small">Hidden from your lists — still here if you need them</span>
        </span>
      </button>

      {open && (
        <>
          {error && <p className="form-error">{error}</p>}
          <ul className="archived-list">
            {customers.map((c) => {
              const n = countsFor(c);
              const busy = busyId === c.id;
              return (
                <li key={c.id} className="archived-row">
                  <div className="archived-row-body">
                    <strong>{c.name}</strong>
                    <span className="muted small">
                      {formatPhone(c.phone) || c.email || '—'}
                      {loaded && (
                        <>
                          {' · '}{n.vehicles} vehicle{n.vehicles === 1 ? '' : 's'}
                          {' · '}{n.jobs} job{n.jobs === 1 ? '' : 's'}
                        </>
                      )}
                      {c.archivedAt && <> · archived {when(c.archivedAt)}</>}
                    </span>
                  </div>
                  <div className="archived-row-actions">
                    <button
                      type="button"
                      className="btn-link"
                      disabled={busy}
                      onClick={() => handleRestore(c)}
                    >
                      {busy ? <Loader2 size={13} className="spin" /> : <Undo2 size={13} />} Restore
                    </button>
                    <button
                      type="button"
                      className="btn-link"
                      disabled={!loaded}
                      onClick={() => exportOne(c)}
                      title="Download everything for this customer"
                    >
                      <Download size={13} /> Export
                    </button>
                    <button
                      type="button"
                      className="btn-link danger"
                      disabled={busy || !loaded}
                      onClick={() => setPendingPurge(c)}
                    >
                      <Trash2 size={13} /> Delete forever
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {pendingPurge && (
        <ConfirmDialog
          title={`Permanently delete ${pendingPurge.name}?`}
          body={
            `This erases them, ${countsFor(pendingPurge).vehicles} vehicle(s), `
            + `${countsFor(pendingPurge).jobs} job(s), every inspection and every photo. `
            + `It cannot be undone and it is not covered by Restore.\n\n`
            + `Close this and tap Export first if you want to keep a copy.`
          }
          confirmLabel="Delete forever"
          cancelLabel="Keep archived"
          tone="danger"
          onConfirm={() => {
            const c = pendingPurge;
            setPendingPurge(null);
            handlePurge(c);
          }}
          onCancel={() => setPendingPurge(null)}
        />
      )}
    </section>
  );
}
